import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useUniverseStore } from './store/universeStore';
import SpaceBackdrop from './components/SpaceBackdrop';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import EmailLinkPage from './pages/EmailLinkPage';
import PlanetPage from './pages/PlanetPage';
import GalaxyPage from './pages/GalaxyPage';
import ProfilePage from './pages/ProfilePage';
import ZonePage from './pages/ZonePage';
import SciencesPage from './pages/SciencesPage';
import ShipyardPage from './pages/ShipyardPage';
import RecyclingPage from './pages/RecyclingPage';
import StarportPage from './pages/StarportPage';
import FleetsPage from './pages/FleetsPage';
import TeamPlanetFleetsPage from './pages/TeamPlanetFleetsPage';
import TeamPlanetPage from './pages/TeamPlanetPage';
import TeamPlanetZonePage from './pages/TeamPlanetZonePage';
import TeamPlanetSciencesPage from './pages/TeamPlanetSciencesPage';
import TeamPlanetShipyardPage from './pages/TeamPlanetShipyardPage';
import TeamPlanetStarportPage from './pages/TeamPlanetStarportPage';
import TeamPlanetLayout from './components/teamPlanet/TeamPlanetLayout';
import BattleReportPage from './pages/BattleReportPage';
import BattleSimulatorPage from './pages/BattleSimulatorPage';
import RankingPage from './pages/RankingPage';
import AlliancePage from './pages/AlliancePage';
import PlayerProfilePage from './pages/PlayerProfilePage';
import PremiumPage from './pages/PremiumPage';
import TradeCenterPage from './pages/TradeCenterPage';
import RaceSelection from './components/RaceSelection';
import UniverseSelection from './components/UniverseSelection';
import AppShell from './components/AppShell';
import TokensPage from './pages/dev/TokensPage';
import { isLegalPath } from './content/legal/paths';
import LegalPageUnavailable from './components/legal/LegalPageUnavailable';

// The fallback is bundled, not lazy: after a redeploy its chunk would be gone too.
const LegalPage = lazy(() =>
    import('./pages/LegalPage').catch(() => ({ default: LegalPageUnavailable })),
);

function App() {
    const { user, profile, isLoading } = useAuthStore();
    const selectedUniverseId = useUniverseStore((s) => s.selectedUniverseId);
    const location = useLocation();

    if (isLoading) {
        return (
            <div className="min-h-screen bg-space-900 flex items-center justify-center">
                <div className="text-content-muted text-heading">Loading...</div>
            </div>
        );
    }

    if (import.meta.env.DEV && location.pathname === '/dev/tokens') {
        return <TokensPage />;
    }

    const legalPath = location.pathname.replace(/\/+$/, '').toLowerCase();
    if (isLegalPath(legalPath)) {
        return (
            <div className="min-h-screen">
                <SpaceBackdrop variant="neutral" />
                <Suspense fallback={null}>
                    <LegalPage path={legalPath} />
                </Suspense>
            </div>
        );
    }

    // Universe selection comes first: a player can hold progress in several
    // universes at once (issue #53), so which one applies to `profile.race`
    // below isn't knowable — and therefore isn't fetchable — until this is
    // picked for the session. Both pickers use the 'neutral' SpaceBackdrop
    // variant directly (issue #182) — they render outside <Routes>, so
    // there's no route for getSpaceBackdropVariant to key off.
    if (user && !selectedUniverseId) {
        return (
            <div className="min-h-screen">
                <SpaceBackdrop variant="neutral" />
                <UniverseSelection />
            </div>
        );
    }

    if (user && !profile?.race) {
        return (
            <div className="min-h-screen">
                <SpaceBackdrop variant="neutral" />
                <RaceSelection />
            </div>
        );
    }

    const loggedOutRoutes = (
        <>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/login/email-link" element={<EmailLinkPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
        </>
    );

    const loggedInRoutes = (
        <Route element={<AppShell />}>
            <Route path="/planet" element={<PlanetPage />} />
            <Route path="/planet/:planetId" element={<PlanetPage />} />
            <Route path="/galaxy" element={<GalaxyPage />} />
            <Route path="/galaxy/:galaxy/:system" element={<GalaxyPage />} />
            <Route path="/fleets" element={<FleetsPage />} />
            <Route path="/team-planet/fleets" element={<TeamPlanetFleetsPage />} />
            <Route path="/team-planet" element={<TeamPlanetLayout />}>
                <Route index element={<TeamPlanetPage />} />
                <Route path="zone/:zoneId" element={<TeamPlanetZonePage />} />
                <Route path="sciences" element={<TeamPlanetSciencesPage />} />
                <Route path="shipyard" element={<TeamPlanetShipyardPage />} />
                <Route path="starport" element={<TeamPlanetStarportPage />} />
            </Route>
            <Route path="/fleets/battle-report/:logId" element={<BattleReportPage />} />
            <Route path="/fleets/simulator" element={<BattleSimulatorPage />} />
            <Route path="/zone/:zoneId" element={<ZonePage />} />
            <Route path="/sciences" element={<SciencesPage />} />
            <Route path="/shipyard" element={<ShipyardPage />} />
            <Route path="/recycling" element={<RecyclingPage />} />
            <Route path="/starport" element={<StarportPage />} />
            <Route path="/trade-center" element={<TradeCenterPage />} />
            <Route path="/personal" element={<ProfilePage />} />
            <Route path="/alliance" element={<AlliancePage />} />
            <Route path="/alliance/:allianceId" element={<AlliancePage />} />
            <Route path="/ranking" element={<RankingPage />} />
            <Route path="/premium" element={<PremiumPage />} />
            <Route path="/player/:userId" element={<PlayerProfilePage />} />
            <Route path="*" element={<Navigate to="/planet" replace />} />
        </Route>
    );

    // Logged-in routes are wrapped in AppShell, which mounts its own
    // route-keyed SpaceBackdrop — mounting a second one here would double up.
    return (
        <div className="min-h-screen">
            {!user && <SpaceBackdrop variant="neutral" />}
            <Routes>
                {!user ? loggedOutRoutes : loggedInRoutes}
            </Routes>
        </div>
    );
}

export default App;
